/**
 * Multi-profile management — W10-A
 *
 * Storage layout:
 *   eternal-fortress.family           → JSON<FamilyState>
 *   eternal-fortress.progress.<id>   → JSON<PlayerProgress>  (per profile)
 *
 * The legacy key `eternal-fortress.progress` is migrated to
 * `eternal-fortress.progress.default` on first access (see progress.ts v13 migration).
 */

import type { FamilyState, ProfileMeta, AvatarColor } from './types';
import { AVATAR_COLORS } from './types';

const FAMILY_KEY = 'eternal-fortress.family';

// ── uuid-lite ─────────────────────────────────────────────────────────────────

function generateId(): string {
  // crypto.randomUUID() is available in modern browsers and Node ≥ 14.17
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// ── FamilyState helpers ───────────────────────────────────────────────────────

export function loadFamilyState(): FamilyState | null {
  const raw = localStorage.getItem(FAMILY_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as FamilyState;
  } catch {
    return null;
  }
}

export function saveFamilyState(state: FamilyState): void {
  localStorage.setItem(FAMILY_KEY, JSON.stringify(state));
}

/**
 * Return the active profile id.
 * If no FamilyState exists yet, returns 'default' (the legacy / migrated profile).
 */
export function getCurrentProfileId(): string {
  return loadFamilyState()?.activeProfileId ?? 'default';
}

/**
 * List all profile metadata. Returns at least the 'default' profile if no
 * FamilyState has been written yet.
 */
export function listProfiles(): ProfileMeta[] {
  const state = loadFamilyState();
  if (!state) {
    // No multi-profile setup yet — synthesise a single 'default' entry
    return [
      {
        id: 'default',
        nickname: '指揮官',
        createdAt: Date.now(),
        avatarColor: AVATAR_COLORS[0],
      },
    ];
  }
  return state.profiles;
}

/**
 * Switch the active profile. Persists the change to FamilyState.
 * Throws if `id` is not a known profile.
 */
export function switchProfile(id: string): void {
  const state = loadFamilyState();
  if (!state) {
    throw new Error(`switchProfile: no FamilyState found; cannot switch to '${id}'`);
  }
  const exists = state.profiles.some((p) => p.id === id);
  if (!exists) {
    throw new Error(`switchProfile: profile '${id}' not found`);
  }
  state.activeProfileId = id;
  saveFamilyState(state);
}

/**
 * Create a new profile with a fresh empty progress record.
 * Returns the new ProfileMeta.
 *
 * NOTE: This does NOT create a PlayerProgress entry — the caller (or
 * loadProgress from progress.ts) will create a fresh one on first access.
 */
export function createProfile(nickname: string, avatarColor?: AvatarColor): ProfileMeta {
  let state = loadFamilyState();

  // If no state exists yet, bootstrap it around the existing 'default' profile
  if (!state) {
    state = {
      profiles: [
        {
          id: 'default',
          nickname: '指揮官',
          createdAt: Date.now(),
          avatarColor: AVATAR_COLORS[0],
        },
      ],
      activeProfileId: 'default',
    };
  }

  const newProfile: ProfileMeta = {
    id: generateId(),
    nickname,
    createdAt: Date.now(),
    avatarColor: avatarColor ?? pickNextColor(state.profiles),
  };

  state.profiles.push(newProfile);
  saveFamilyState(state);
  return newProfile;
}

/** Pick a colour not yet used by existing profiles (cycles through AVATAR_COLORS). */
function pickNextColor(existing: ProfileMeta[]): AvatarColor {
  const usedColors = new Set(existing.map((p) => p.avatarColor));
  for (const color of AVATAR_COLORS) {
    if (!usedColors.has(color)) return color;
  }
  // All colours used — fall back to first
  return AVATAR_COLORS[0];
}

/**
 * Ensure FamilyState exists, migrating the legacy single-profile storage if
 * needed. Call this once at app startup (before loadProgress).
 *
 * Returns the active profile id after ensuring state is consistent.
 */
export function ensureFamilyState(): string {
  const state = loadFamilyState();
  if (state) return state.activeProfileId;

  // Bootstrap: create FamilyState pointing at the legacy 'default' profile
  const initial: FamilyState = {
    profiles: [
      {
        id: 'default',
        nickname: '指揮官',
        createdAt: Date.now(),
        avatarColor: AVATAR_COLORS[0],
      },
    ],
    activeProfileId: 'default',
  };
  saveFamilyState(initial);
  return 'default';
}
